import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { PaymentsSubscriptionsList } from '@dashboard/scenes/PaymentsSubscriptions';
import CardContent from '@dashboard/components/CardContent';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import ExportView from '@dashboard/components/ExportView';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import SortInput from '@dashboard/components/Form/components/SortInput';
import { stringify } from 'query-string';

const SORT_DIRECTIONS = {
  ASCENDING: '1',
  DESCENDING: '-1',
};

const SORT_OPTIONS = {
  NEWEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Newest first',
  },
  OLDEST_FIRST: {
    value: 'createdAt',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Oldest first',
  },
  EMAIL_ASCENDING: {
    value: 'email',
    direction: SORT_DIRECTIONS.ASCENDING,
    label: 'Email A-Z',
  },
  EMAIL_DESCENDING: {
    value: 'email',
    direction: SORT_DIRECTIONS.DESCENDING,
    label: 'Email Z-A',
  },
};

const FILTER_OPTIONS = {
  ALL: {
    label: 'All',
    value: 'all',
  },
  ACTIVE: {
    label: 'Active',
    value: 'active',
  },
  RECOVERED: {
    label: 'Recovered',
    value: 'recovered',
  },
  TRIALING: {
    label: 'Trialing',
    value: 'trialing',
  },
  SCHEDULED: {
    label: 'Scheduled',
    value: 'scheduled',
  },
  COMPLETED: {
    label: 'Completed',
    value: 'completed',
  },
  CANCELED: {
    label: 'Canceled',
    value: 'canceled',
  },
  PAST_DUE: {
    label: 'Overdue',
    value: 'past_due',
  },
  INCOMPLETE: {
    label: 'Incomplete',
    value: 'incomplete',
  },
  FAILED: {
    label: 'Failed',
    value: 'failed',
  },
  ABANDONED: {
    value: 'abandoned',
    label: 'Abandoned',
  },
};

function getSortOptionFromQuery(sortBy, sortDirection) {
  const sortOption = Object.keys(SORT_OPTIONS).find(
    key => SORT_OPTIONS[key].value === sortBy && SORT_OPTIONS[key].direction === sortDirection,
  );

  return sortOption || Object.keys(SORT_OPTIONS)[0];
}

function getStatusFromQuery(status) {
  const statusOption = Object.keys(FILTER_OPTIONS).find(
    key => FILTER_OPTIONS[key].value === status,
  );

  return statusOption || Object.keys(FILTER_OPTIONS)[0];
}

const PageSubscriptions = ({ page }) => {
  const history = useHistory();
  const query = useUrlQuery();

  const [sortOption, setSortOption] = useState(
    getSortOptionFromQuery(query.sortBy, query.sortDirection),
  );
  const [search, setSearch] = useState(query.search || '');
  const [status, setStatus] = useState(getStatusFromQuery(query.status));

  useEffect(() => {
    setSearch(query.search || '');
  }, [query.search]);

  useEffect(() => {
    setSortOption(getSortOptionFromQuery(query.sortBy, query.sortDirection));
  }, [query.sortBy, query.sortDirection]);

  useEffect(() => {
    setStatus(getStatusFromQuery(query.status));
  }, [query.status]);

  const sortBy = SORT_OPTIONS[sortOption].value;
  const sortDirection = SORT_OPTIONS[sortOption].direction;

  const sort = {
    [sortBy]: sortDirection,
  };

  const filter = {
    ...(FILTER_OPTIONS[status].value !== 'all' && {
      status: FILTER_OPTIONS[status].value,
    }),
    page: page._id,
    livemode: true,
  };

  useEffect(() => {
    const params = {};

    params.sortBy = sortBy;
    params.sortDirection = sortDirection;

    if (search) {
      params.search = search;
    }

    if (FILTER_OPTIONS[status].value !== 'all') {
      params.status = FILTER_OPTIONS[status].value;
    }

    history.replace({
      search: stringify(params),
    });
  }, [sortBy, sortDirection, status, search, page]);

  return (
    <div className="mb-24 overflow-auto">
      <div className="flex items-center justify-between pb-4">
        <h2 className="text-xl font-medium">Subscriptions</h2>

        <div className="flex items-center gap-x-2">
          <SearchInput
            onChange={setSearch}
            name="search_customers"
            placeholder="Search by email"
            initialValue={search}
          />

          <FilterInput options={FILTER_OPTIONS} onChange={setStatus} value={status} />

          <SortInput options={SORT_OPTIONS} onChange={setSortOption} value={sortOption} />

          <ExportView
            collection="subscriptions"
            label="subscriptions"
            filters={{
              ...filter,
              page: page._id,
              sort: JSON.stringify(sort),
            }}
          />
        </div>
      </div>

      <CardContent className="overflow-auto mb-20">
        <CardListItem variant="header" className="!bg-gray-50 !mt-0 rounded-t">
          <CardListItemElement size="3xl">Customer</CardListItemElement>
          <CardListItemElement size="xl">Amount</CardListItemElement>
          <CardListItemElement size="xl" align="right">
            Date
          </CardListItemElement>
          <CardListItemElement size="large">Abandonment</CardListItemElement>
          <CardListItemElement size="medium">Status</CardListItemElement>
        </CardListItem>

        <PaymentsSubscriptionsList
          sort={sort}
          search={search}
          filter={filter}
          options={{
            hidePaymentLink: true,
            hidePageType: true,
          }}
        />
      </CardContent>
    </div>
  );
};

PageSubscriptions.propTypes = {
  page: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

export default PageSubscriptions;
